import React, { useState, useEffect, useContext } from 'react';
import { usersAPI, tenantsAPI } from '../../services/api';
import PaginatedTable from '../../components/PaginatedTable';
import { useI18n } from '../../i18n/I18nContext';

const UsersManager = () => {
    const { t } = useI18n();
    const [users, setUsers] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({ 
        name: '', 
        email: '', 
        password: '', 
        role: 'admin', 
        tenant_id: '',
        phone: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [uRes, tRes] = await Promise.all([
                usersAPI.list(),
                tenantsAPI.superList()
            ]);
            setUsers(uRes.data);
            setTenants(tRes.data);
        } catch (err) {
            console.error('Error fetching data:', err);
        }
        setLoading(false);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            // Create a custom request since superadmin needs to specify tenant_id explicitly
            // and the usersAPI.create uses req.user.tenant_id in backend.
            // But wait, the backend uses req.user.tenant_id. 
            // We should check if we need a superadmin specific user creation route.
            
            // For now, let's assume we use a specialized route or the backend is updated.
            // Actually, I'll update the backend to allow superadmins to specify tenant_id.
            
            if (editingUser) {
                await usersAPI.update(editingUser.id, formData);
            } else {
                await usersAPI.create(formData);
            }
            setShowModal(false);
            setEditingUser(null);
            setFormData({ name: '', email: '', password: '', role: 'admin', tenant_id: '', phone: '' });
            fetchData();
        } catch (err) {
            alert(err.response?.data?.error || 'Error saving user');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Are you sure?'))) return;
        try {
            await usersAPI.remove(id);
            fetchData();
        } catch (err) {
            alert('Error deleting user');
        }
    };

    const columns = [
        { label: t('Name'), key: 'name' },
        { label: t('Email'), key: 'email' },
        { label: t('Role'), key: 'role' },
        { 
            label: t('Tenant'), 
            key: 'tenant_id', 
            render: (val) => tenants.find(t => t.id === val)?.name || val 
        },
        {
            label: t('Actions'),
            key: 'actions',
            render: (_, row) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => {
                            setEditingUser(row);
                            setFormData({ 
                                name: row.name, 
                                email: row.email, 
                                password: '', 
                                role: row.role, 
                                tenant_id: row.tenant_id,
                                phone: row.phone || ''
                            });
                            setShowModal(true);
                        }}
                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition"
                    >
                        {t('Edit')}
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition"
                    >
                        {t('Delete')}
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="p-8 bg-gray-50 min-h-screen">
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-8 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('Users Management')}</h1>
                        <p className="text-gray-500 mt-1">{t('Global user administrative control')}</p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingUser(null);
                            setFormData({ name: '', email: '', password: '', role: 'admin', tenant_id: '', phone: '' });
                            setShowModal(true);
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-semibold shadow-md transition-all active:scale-95 flex items-center gap-2"
                    >
                        <span className="text-xl">+</span> {t('Add User')}
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <PaginatedTable
                        columns={columns}
                        data={users}
                        loading={loading}
                    />
                </div>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
                        <div className="bg-indigo-600 px-6 py-4">
                            <h2 className="text-xl font-bold text-white">
                                {editingUser ? t('Edit User') : t('Add User')}
                            </h2>
                        </div>
                        <form onSubmit={handleSave} className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('Name')}</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-outline"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('Email')}</label>
                                    <input
                                        type="email"
                                        className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-outline"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('Password')}</label>
                                    <input
                                        type="password"
                                        className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-outline"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required={!editingUser}
                                        placeholder={editingUser ? t('Leave blank to keep current') : ''}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">{t('Tenant')}</label>
                                        <select
                                            className="w-full border border-gray-300 rounded-lg p-2.5 bg-white focus:ring-2 focus:ring-indigo-500"
                                            value={formData.tenant_id}
                                            onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                                            required
                                        >
                                            <option value="">{t('Select Tenant')}</option>
                                            {tenants.map(tn => (
                                                <option key={tn.id} value={tn.id}>{tn.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">{t('Role')}</label>
                                        <select
                                            className="w-full border border-gray-300 rounded-lg p-2.5 bg-white focus:ring-2 focus:ring-indigo-500"
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                            required
                                        >
                                            <option value="admin">{t('Admin')}</option>
                                            <option value="driver">{t('Driver')}</option>
                                            <option value="parent">{t('Parent')}</option>
                                            <option value="superadmin">{t('Superadmin')}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-8">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition"
                                >
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition"
                                >
                                    {t('Save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersManager;
